import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import { fetchBlogPosts, formatDate, getExcerpt, getFeaturedImage } from "../lib/wordpressApi";
import { getImageLoading, handleImageError } from "../lib/imageOptimization";

const PER_PAGE = 9;

const Blogs = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const loadPosts = useCallback(async (page) => {
    try {
      setLoading(true);
      setError(null);
      const { posts: fetchedPosts, totalPages: fetchedTotalPages } = await fetchBlogPosts(page, PER_PAGE, true);
      setPosts(fetchedPosts);
      setFilteredPosts(fetchedPosts);
      setTotalPages(fetchedTotalPages || 1);
    } catch (err) {
      setError(err.message || "Failed to load blog posts. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts(currentPage);
  }, [currentPage, loadPosts]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPosts(posts);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = posts.filter((post) => {
      const title = (post?.title?.rendered || "").toLowerCase();
      const excerpt = (post?.excerpt?.rendered || "").toLowerCase();
      const content = (post?.content?.rendered || "").toLowerCase();
      return title.includes(query) || excerpt.includes(query) || content.includes(query);
    });
    setFilteredPosts(filtered);
  }, [searchQuery, posts]);

  const hasPosts = useMemo(() => Array.isArray(filteredPosts) && filteredPosts.length > 0, [filteredPosts]);

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          type="button"
          onClick={() => goToPage(i)}
          disabled={loading}
          className={`h-9 w-9 rounded-full text-sm font-semibold transition ${
            i === currentPage
              ? "bg-primary text-white"
              : "border border-primary/30 text-primary hover:bg-primary/5"
          } disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-canvas pb-20 text-ink overflow-x-hidden">
      <section className="relative overflow-hidden border-b border-primary/10 bg-white">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-white to-white" />
        <div className="relative mx-auto flex max-w-5xl flex-col items-center gap-3 sm:gap-4 px-4 sm:px-6 py-10 sm:py-12 text-center lg:px-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">Blogs</h1>
          <p className="max-w-3xl text-xs sm:text-sm lg:text-base leading-relaxed text-ink-muted px-2">
            Fresh perspectives on language learning, cultural intelligence, productivity, and success stories from The Lingua Alliance community.
          </p>
        </div>
      </section>

      <section className="mx-auto mt-12 sm:mt-16 w-full max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold">Latest Articles</h2>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-64 rounded-full border border-primary/20 bg-white py-2 pl-10 pr-4 text-sm placeholder:text-ink-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        {error && (
          <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading && (
          <div className="mt-10 flex justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
          </div>
        )}

        {!loading && !error && !hasPosts && (
          <div className="mt-10 rounded-lg border border-primary/10 bg-white px-4 py-6 text-center text-ink-muted">
            {searchQuery ? "No posts match your search." : "No blog posts found. Please check back soon."}
          </div>
        )}

        <div className="mt-6 sm:mt-8 space-y-5 sm:space-y-6">
          {hasPosts && filteredPosts.map((post, index) => {
            const imageUrl = getFeaturedImage(post, "medium_large");
            const excerpt = getExcerpt(post, 180);
            const date = formatDate(post?.date);
            const postUrl = `/blogs/${post?.slug}`;

            return (
              <article
                key={post.id || post.slug || index}
                onClick={() => navigate(postUrl)}
                className="cursor-pointer rounded-xl sm:rounded-2xl border border-primary/10 bg-white overflow-hidden shadow-elevated transition hover:-translate-y-1 hover:shadow-float"
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-72 h-44 sm:h-48 md:h-auto bg-gradient-to-br from-primary/20 to-primary-soft/10 flex-shrink-0">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        loading={getImageLoading(index)}
                        alt={post?.title?.rendered || "Blog cover"}
                        onError={(e) => handleImageError(e)}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-primary">No image</div>
                    )}
                  </div>
                  <div className="p-5 sm:p-6 flex-1">
                    <span className="inline-flex rounded-full bg-primary/10 px-2.5 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.15em] sm:tracking-[0.2em] text-primary">
                      {post?._embedded?.author?.[0]?.name || "The Lingua Alliance"}
                    </span>
                    <h3 className="mt-2 sm:mt-3 text-base sm:text-lg font-semibold text-ink" dangerouslySetInnerHTML={{ __html: post?.title?.rendered || "Untitled" }} />
                    <p className="mt-2 sm:mt-3 text-xs sm:text-sm leading-relaxed text-ink-muted">{excerpt}</p>
                    <div className="mt-3 sm:mt-4 flex items-center justify-between text-xs sm:text-sm text-ink-muted">
                      <span>{date}</span>
                      <span className="inline-flex items-center font-semibold text-primary">
                        Read More →
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {!searchQuery && hasPosts && totalPages > 1 && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={handlePrev}
              disabled={currentPage === 1 || loading}
              className="rounded-full border border-primary/30 px-4 py-2 text-sm font-semibold text-primary transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-primary/5"
            >
              Previous
            </button>
            {renderPageNumbers()}
            <button
              type="button"
              onClick={handleNext}
              disabled={currentPage === totalPages || loading}
              className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-primary/90"
            >
              Next
            </button>
          </div>
        )}

        {!searchQuery && hasPosts && totalPages > 1 && (
          <p className="mt-4 text-center text-xs text-ink-muted">
            Page {currentPage} of {totalPages}
          </p>
        )}
      </section>
    </div>
  );
};

export default Blogs;

